import type { PhysicsEvent } from './physics/world';
import type { TableDefinition } from './tables/definition';

const MAX_MULTIPLIER = 5;

/** Applies table-agnostic score rules to the event stream from physics. */
export class ScoreTracker {
  private rolloverGroups = new Map<string, Set<string>>();
  private completedRollovers = new Map<string, Set<string>>();
  private temporaryMultiplier = 1;
  score = 0;
  multiplier = 1;

  reset(table: TableDefinition): void {
    this.score = 0;
    this.multiplier = 1;
    this.temporaryMultiplier = 1;
    this.rolloverGroups.clear();
    this.completedRollovers.clear();

    for (const part of table.parts) {
      if (part.kind !== 'rollover' || !part.laneId) continue;
      const members = this.rolloverGroups.get(part.laneId) ?? new Set<string>();
      members.add(part.id);
      this.rolloverGroups.set(part.laneId, members);
    }
  }

  setTemporaryMultiplier(multiplier: number): void {
    this.temporaryMultiplier = Number.isFinite(multiplier) ? Math.max(1, multiplier) : 1;
  }

  apply(event: PhysicsEvent): void {
    if (event.type === 'score') {
      this.score += event.points * this.multiplier * this.temporaryMultiplier;
      return;
    }
    if (event.type !== 'rollover' || !event.laneId) return;

    const members = this.rolloverGroups.get(event.laneId);
    if (!members) return;
    const completed = this.completedRollovers.get(event.laneId) ?? new Set<string>();
    completed.add(event.partId);
    if (completed.size < members.size) {
      this.completedRollovers.set(event.laneId, completed);
      return;
    }
    this.multiplier = Math.min(MAX_MULTIPLIER, this.multiplier + 1);
    this.completedRollovers.delete(event.laneId);
  }

  /** Rules/modes award fixed bonuses outside the raw mechanism score stream. */
  addBonus(points: number): void {
    if (!Number.isFinite(points) || points <= 0) return;
    this.score += Math.floor(points);
  }
}
